import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SEOData from '@/components/feature/SEOData';
import MangaCard from '@/components/base/MangaCard';
import { mangas, categories } from '@/mocks/mangas';
import { useScrollToTop } from '@/hooks/useScrollToTop';

const sortOptions = [
  { value: 'latest', label: '最新更新' },
  { value: 'popular', label: '最受欢迎' },
  { value: 'rating', label: '评分最高' },
];

export default function Category() {
  useScrollToTop();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'all';
  const [activeCategory, setActiveCategory] = useState(initialType);
  const [sortBy, setSortBy] = useState('latest');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setActiveCategory(initialType);
  }, [initialType]);

  const filteredMangas = useMemo(() => {
    let result = activeCategory === 'all' ? [...mangas] : mangas.filter((m) => m.category === activeCategory);

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(query) ||
          m.author.toLowerCase().includes(query) ||
          m.tags.some((t) => t.toLowerCase().includes(query))
      );
    }

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
        break;
    }

    return result;
  }, [activeCategory, sortBy, searchQuery]);

  const handleCategoryChange = (catId: string) => {
    setActiveCategory(catId);
    setSearchParams(catId === 'all' ? {} : { type: catId });
  };

  const currentCategory = categories.find((c) => c.id === activeCategory);
  const pageTitle = activeCategory === 'all'
    ? '分类浏览 - 龙禁漫 | 全部漫画资源在线浏览'
    : `${currentCategory?.name || ''}漫画 - 龙禁漫 | ${currentCategory?.description || ''}`;

  const pageDescription = activeCategory === 'all'
    ? '浏览龙禁漫全部漫画分类，热血、恋爱、悬疑、奇幻、搞笑、科幻、恐怖、武侠、日常等多元题材任你选择，发现你的下一部心头好。'
    : `龙禁漫${currentCategory?.name || ''}漫画分类 - ${currentCategory?.description || ''}。海量正版${currentCategory?.name || ''}漫画资源，立即在线阅读。`;

  return (
    <>
      <SEOData
        pageType="category"
        title={pageTitle}
        description={pageDescription}
        canonical={`https://longjinman.com/category${activeCategory !== 'all' ? `?type=${activeCategory}` : ''}`}
      />
      <Navbar />
      <div className="pt-16 md:pt-18">
        <Breadcrumbs />
      </div>
      <main>
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {activeCategory === 'all' ? '全部漫画' : currentCategory?.name + '漫画'}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base">
                {activeCategory === 'all'
                  ? '探索龙禁漫全部漫画资源，找到你最喜欢的作品'
                  : currentCategory?.description}
              </p>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="relative w-full sm:w-80">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-search-line text-foreground-400 text-sm"></i>
                </span>
                <input
                  type="text"
                  placeholder="搜索漫画名称、作者或标签..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                />
              </div>
              <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all duration-200 ${
                      sortBy === opt.value
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredMangas.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {filteredMangas.map((manga) => (
                  <MangaCard
                    key={manga.id}
                    id={manga.id}
                    title={manga.title}
                    author={manga.author}
                    cover={manga.cover}
                    category={manga.category}
                    rating={manga.rating}
                    tags={manga.tags}
                    status={manga.status}
                    views={manga.views}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 mx-auto flex items-center justify-center mb-4">
                  <i className="ri-search-line text-foreground-300 text-4xl"></i>
                </span>
                <p className="text-foreground-500 text-base mb-2">没有找到相关漫画</p>
                <p className="text-foreground-400 text-sm">试试调整搜索关键词或切换分类</p>
              </div>
            )}

            {filteredMangas.length > 0 && (
              <div className="mt-8 text-center text-foreground-400 text-sm">
                共找到 {filteredMangas.length} 部漫画
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}